import {
  FolderTwoTone,
  HomeTwoTone,
  LeftOutlined,
  ReadOutlined,
  RightOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { Button, Layout, Menu } from 'antd';
import { ItemType, MenuItemType } from 'antd/lib/menu/interface';
import { ReactComponent as BarGraphIcon } from 'assets/icons/BarGraph.svg';
import { ReactComponent as EuroBlueIcon } from 'assets/icons/EuroBlue.svg';
import { ReactComponent as MapMenuIcon } from 'assets/icons/MapMenu.svg';
import { ReactComponent as PresIcon } from 'assets/icons/Presentation.svg';
import { useAuthContext } from 'contexts/AuthContext';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { AccessType } from 'types/User';
import Colors from '../const/colors';
const { Sider } = Layout;

function getColor(condition: boolean) {
  return condition ? Colors.BleuHellio : Colors.GrisClair;
}

interface SideMenuProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  isImpersonated: boolean;
}

const SideMenu: React.FC<SideMenuProps> = ({
  collapsed,
  setCollapsed,
  isImpersonated,
}) => {
  const { t } = useTranslation();
  const { auth } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const isConcessionnaire =
    auth?.user?.accessType === AccessType.CONCESSIONNAIRE;
  const [manualOpenMenuKeys, setManualOpenMenuKeys] = React.useState<string[]>(
    () => (location.pathname.startsWith('/dossiers') ? ['/dossiers'] : []),
  );

  const handleCollapse = (value: boolean) => {
    setCollapsed(value);
  };

  const getActiveRoute = () => {
    if (location.pathname === '/') return '/';
    if (location.pathname.startsWith('/dossiers')) return '/dossiers';
    if (location.pathname.startsWith('/ressources')) return '/ressources';
    if (location.pathname.startsWith('/tableau-des-primes')) {
      return '/tableau-des-primes';
    }
    return location.pathname;
  };

  const activeRoute = getActiveRoute();

  const dossiersViewMode = React.useMemo(() => {
    if (location.pathname !== '/dossiers') {
      return null;
    }

    const hashViewMode = location.hash.replace('#', '');
    if (
      hashViewMode === 'table' ||
      hashViewMode === 'chart' ||
      hashViewMode === 'status' ||
      hashViewMode === 'map'
    ) {
      return hashViewMode;
    }

    return 'table';
  }, [location.hash, location.pathname]);

  const activeMenuKey = React.useMemo(() => {
    if (dossiersViewMode) {
      return `/dossiers#${dossiersViewMode}`;
    }

    return activeRoute;
  }, [activeRoute, dossiersViewMode]);

  const menuLabelClassName = 'text-[14px] leading-[22px] font-medium';

  const menuItems: ItemType<MenuItemType>[] = [
    {
      key: '/',
      icon: <HomeTwoTone twoToneColor={getColor(activeRoute === '/')} />,
      label: <span className={menuLabelClassName}>{t('menu:home')}</span>,
    },
    {
      key: '/dossiers',
      icon: (
        <FolderTwoTone twoToneColor={getColor(activeRoute === '/dossiers')} />
      ),
      label: <span className={menuLabelClassName}>{t('menu:dossiers')}</span>,
      children: [
        {
          key: '/dossiers#table',
          icon: <TableOutlined />,
          label: (
            <span className={menuLabelClassName}>
              {t('global:vue_tableau')}
            </span>
          ),
        },
        ...(!isConcessionnaire
          ? [
              {
                key: '/dossiers#chart',
                icon: <PresIcon />,
                label: (
                  <span className={menuLabelClassName}>
                    {t('global:vue_graphique')}
                  </span>
                ),
              },
            ]
          : []),
        {
          key: '/dossiers#status',
          icon: <BarGraphIcon />,
          label: (
            <span className={menuLabelClassName}>{t('global:vue_statut')}</span>
          ),
        },
        {
          key: '/dossiers#map',
          icon: <MapMenuIcon />,
          label: (
            <span className={menuLabelClassName}>{t('global:vue_carte')}</span>
          ),
        },
      ],
    },
    {
      key: '/ressources',
      icon: <ReadOutlined color={getColor(activeRoute === '/ressources')} />,
      label: <span className={menuLabelClassName}>{t('menu:ressources')}</span>,
    },
    ...(isConcessionnaire
      ? [
          {
            key: '/tableau-des-primes',
            icon: (
              <EuroBlueIcon
                style={{
                  color: getColor(activeRoute === '/tableau-des-primes'),
                }}
              />
            ),
            label: t('menu:primes_table'),
          },
        ]
      : []),
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key.startsWith('/dossiers#')) {
      const viewMode = key.split('#')[1];
      navigate({
        pathname: '/dossiers',
        search: location.search,
        hash: viewMode ? `#${viewMode}` : '',
      });
      return;
    }

    navigate(key);
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={handleCollapse}
      width={205}
      collapsedWidth={80}
      trigger={null}
      data-is-impersonated={isImpersonated ? 'true' : 'false'}
      className="hidden bg-white border-r border-gray-200 sticky top-16 h-[calc(100dvh-64px)] overflow-y-auto z-40 self-start sm:block"
    >
      <Menu
        mode="inline"
        selectedKeys={[activeMenuKey]}
        openKeys={manualOpenMenuKeys}
        items={menuItems}
        onClick={handleMenuClick}
        onOpenChange={(keys) => setManualOpenMenuKeys([...keys])}
        className="side-menu border-r-0 pt-4"
      />

      <div className="absolute bottom-14 w-full">
        <Button
          type="primary"
          shape="circle"
          icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
          size="small"
          className="absolute -right-0 top-1/2 -translate-y-1/2 !rounded-r-none"
          onClick={() => handleCollapse(!collapsed)}
          aria-label={
            collapsed ? t('menu:expand_sidebar') : t('menu:collapse_sidebar')
          }
        />
      </div>
    </Sider>
  );
};

export default SideMenu;
